/**
 * Tipos de subred del IPAM — fuente única para toda la UI.
 *
 * El catálogo vive en la base de datos (`ipam_subnet_types`) y es **editable**
 * desde la propia pantalla de subredes (botón «Tipos»). La lista de aquí es la
 * semilla y el respaldo si la API no responde; `useSubnetTypes()` la sustituye
 * por la del servidor. Los mapas `SUBNET_TYPE_LABELS` / `SUBNET_TYPE_COLORS` se
 * actualizan **en el sitio** para que los componentes que los importan sigan
 * funcionando sin cambios (leen la propiedad al pintar).
 *
 * `value` = lo que se guarda en BD; `label` = lo que se ve; `color` = variante
 * de badge de Bootstrap (text-bg-<color>).
 *
 * Los routers tienen su propia lista de tipos de interfaz (RouterForm), que es
 * otro concepto (rol de la interfaz, no tipo de red).
 */
import { useCallback, useEffect, useState } from 'react';
import api from '../services/api';

export const SUBNET_TYPES = [
  { value: 'lan',      label: 'LAN',       color: 'primary'   },
  { value: 'wifi',     label: 'WIFI',      color: 'success'   },
  { value: 'guest',    label: 'GUEST',     color: 'secondary' },
  { value: 'dmz',      label: 'DMZ',       color: 'danger'    },
  { value: 'wan',      label: 'WAN',       color: 'info'      },
  { value: 'public',   label: 'PÚBLICA',   color: 'dark'      },
  { value: 'vpn',      label: 'VPN',       color: 'warning'   },
  { value: 'p2p',      label: 'P2P',       color: 'info'      },
  { value: 'voice',    label: 'VoIP',      color: 'success'   },
  { value: 'storage',  label: 'STORAGE',   color: 'dark'      },
  { value: 'oob',      label: 'OOB',       color: 'secondary' },
  { value: 'sync',     label: 'SYNC / HA', color: 'warning'   },
  { value: 'cluster',  label: 'CLUSTER',   color: 'primary'   },
  { value: 'mgmt',     label: 'MGMT',      color: 'dark'      },
  { value: 'loopback', label: 'LOOPBACK',  color: 'secondary' },
  { value: 'iot',      label: 'IOT',       color: 'info'      },
  { value: 'cameras',  label: 'CÁMARAS',   color: 'secondary' },
];

/** Variantes de badge admitidas (el backend valida la misma lista). */
export const SUBNET_TYPE_COLOR_OPTIONS = [
  'primary', 'secondary', 'success', 'danger', 'warning', 'info', 'dark', 'light',
];

export const SUBNET_TYPE_COLORS = SUBNET_TYPES.reduce((acc, t) => {
  acc[t.value] = t.color;
  return acc;
}, {});

export const SUBNET_TYPE_LABELS = SUBNET_TYPES.reduce((acc, t) => {
  acc[t.value] = t.label;
  return acc;
}, {});

let catalog = SUBNET_TYPES.slice();

/**
 * Reemplaza el catálogo en memoria (y los mapas derivados). Si la lista viene
 * vacía, se conserva la semilla: la UI nunca se queda sin tipos.
 */
export function setSubnetTypeCatalog(list) {
  const clean = Array.isArray(list)
    ? list
        .filter((t) => t && t.value)
        .map((t, index) => ({
          value: String(t.value),
          label: t.label ? String(t.label) : String(t.value).toUpperCase(),
          color: t.color ? String(t.color) : 'secondary',
          position: Number.isFinite(t.position) ? t.position : index,
          is_system: Boolean(t.is_system),
          subnets_count: t.subnets_count,
        }))
    : [];
  catalog = clean.length ? clean : SUBNET_TYPES.slice();

  Object.keys(SUBNET_TYPE_LABELS).forEach((key) => delete SUBNET_TYPE_LABELS[key]);
  Object.keys(SUBNET_TYPE_COLORS).forEach((key) => delete SUBNET_TYPE_COLORS[key]);
  catalog.forEach((t) => {
    SUBNET_TYPE_LABELS[t.value] = t.label;
    SUBNET_TYPE_COLORS[t.value] = t.color;
  });
  return catalog;
}

export function getSubnetTypes() {
  return catalog;
}

export function subnetTypeColor(value) {
  return SUBNET_TYPE_COLORS[value] || 'secondary';
}

/**
 * Etiqueta de un tipo de subred. Los tipos desconocidos (datos antiguos o
 * creados por API) se muestran en mayúsculas en vez de quedarse en blanco.
 */
export function subnetTypeLabel(value) {
  if (!value) return '—';
  return SUBNET_TYPE_LABELS[value] || String(value).toUpperCase();
}

/**
 * Carga el catálogo del servidor (una vez por sesión: módulo singleton) y lo
 * devuelve. `reload()` fuerza la recarga tras crear/editar/borrar un tipo.
 */
let loadPromise = null;

export function useSubnetTypes(companyId) {
  const [types, setTypes] = useState(getSubnetTypes());
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    if (!companyId) return getSubnetTypes();
    setLoading(true);
    try {
      const list = await api.getIpamSubnetTypes(companyId);
      const next = setSubnetTypeCatalog(list);
      setTypes(next);
      return next;
    } catch {
      // Sin catálogo del servidor se sigue con la semilla.
      setTypes(getSubnetTypes());
      return getSubnetTypes();
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (!companyId) return undefined;
    if (!loadPromise) {
      loadPromise = api.getIpamSubnetTypes(companyId).catch(() => null);
    }
    let cancelled = false;
    loadPromise.then((list) => {
      if (cancelled || !list) return;
      setTypes(setSubnetTypeCatalog(list));
    });
    return () => { cancelled = true; };
  }, [companyId]);

  return { types, loading, reload };
}
